import React, { useState, useEffect } from 'react';
import InputGroup from '../../input-group';
import keyNames from '../../../constants/key-names';

const DEFAULT_ARROW_OFFSET = 1;

const VALID_KEY_CODES = [keyNames.UP, keyNames.DOWN];
const isValidKeyCode = (key) => VALID_KEY_CODES.includes(key);
const stringifyValue = (value, asFloat) => {
  if (asFloat && typeof value === 'number') {
    return value.toFixed(2);
  }

  return String(value).toUpperCase();
};

const ColorInput = ({
  onChange,
  name,
  value,
  min = undefined,
  max = undefined,
  isValuePercent = false,
  align = 'center',
  asFloat = false,
}) => {
  const [inputValue, setInputValue] = useState(stringifyValue(value, asFloat));
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = (updatedValue) => {
    // Preserve trailing decimal point during typing
    if (asFloat && (updatedValue.endsWith('.') || updatedValue === '')) {
      setInputValue(`${updatedValue}${isValuePercent ? '%' : ''}`);
      return;
    }

    const parsedValue = asFloat ? parseFloat(updatedValue) : Number.parseInt(updatedValue, 10);
    let newValue = Number.isNaN(parsedValue) ? updatedValue : parsedValue;

    if (typeof newValue === 'number') {
      if (typeof min === 'number' && newValue < min) {
        newValue = min;
      }

      if (typeof max === 'number' && newValue > max) {
        newValue = max;
      }

      if (asFloat) {
        newValue = parseFloat(newValue.toFixed(2));
      }
    }

    const stringifiedValue = stringifyValue(newValue);

    setInputValue(`${stringifiedValue}${isValuePercent ? '%' : ''}`);
    onChange({ [name]: stringifiedValue });
  };

  const handleBlur = () => {
    const stringifiedValue = stringifyValue(value, asFloat);

    if (inputValue !== stringifiedValue) {
      setInputValue(stringifiedValue);
    }

    setIsFocused(false);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleKeyDown = (event) => {
    const newValue = asFloat
      ? parseFloat(event.currentTarget.value)
      : Number.parseInt(event.currentTarget.value, 10);

    if (!Number.isNaN(newValue) && isValidKeyCode(event.key)) {
      const updatedValue =
        event.key === keyNames.UP
          ? newValue + DEFAULT_ARROW_OFFSET
          : newValue - DEFAULT_ARROW_OFFSET;

      handleChange(String(updatedValue));
    }
  };

  useEffect(() => {
    const stringifiedValue = stringifyValue(value, asFloat);

    if (stringifiedValue !== inputValue && !isFocused) {
      setInputValue(stringifiedValue);
    }
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [value]);

  return (
    <InputGroup
      value={inputValue}
      align={align}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
    />
  );
};

export default ColorInput;
